import { faSearch } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Group, Stack, useMantineTheme } from '@mantine/core';
import { type IObservableArray } from 'mobx';
import { observer } from 'mobx-react-lite';
import { motion } from 'motion/react';
import type { JSX } from 'react';
import React from 'react';
import { useTranslation } from 'react-i18next';

import type { Campaign, Targeting, TPredicateMode } from '@feathr/blackbox';
import {
  AsyncDisplay,
  CardV2 as Card,
  Checkbox,
  DebouncedInput,
  EmptyState,
  Heading,
  Input,
  SegmentedControl,
  Text,
} from '@feathr/components';

import { getInterestsTargetings } from '../Targetings.utils';
import { INTERESTS_ANIMATIONS } from './Interests.animations';
import { useSearch } from './Interests.useSearch';
import { useSelectedInterests } from './Interests.useSelectedInterests';
import {
  determineInterestsMatchMode,
  getGroupIdForInterest,
  getInterestChannels,
} from './Interests.utils';

interface IProps {
  campaign: Campaign;
  targetings: IObservableArray<Targeting>;
}

function Interests({ campaign, targetings }: Readonly<IProps>): JSX.Element {
  const theme = useMantineTheme();
  const { t } = useTranslation();
  const channel = getInterestChannels(campaign.get('integrations'));
  const {
    isSearching,
    filteredInterests,
    allInterests,
    categories,
    selectedCategory,
    handleSearch,
    handleCategoryChange,
    search,
    isLoading,
  } = useSearch(channel);
  const { handleInterestChange, selectedInterestIds } = useSelectedInterests({
    campaign,
    targetings,
    filteredInterests,
  });

  const interestTargetings = getInterestsTargetings(targetings);

  const numSearchResults = filteredInterests.length;
  const isNotSearching = !isSearching && !search;
  const hasResults = numSearchResults > 0 && search && !isSearching;
  const hasNoResults = numSearchResults === 0 && search && !isSearching;

  /**
   * Selecting between `match_any` or `match_all` requires us to loop over all the relevant
   * interest targeting and reassign the numeric group identifier appropriately.
   *
   * This numeric ID does not need to be globally unique, just unique within the set of interests of the current campaign.
   *
   * - `match_any`: all interests share the same group ID
   * - `match_all`: each interest has a unique group ID
   *
   * @param value - either `match_any` or `match_all`
   */
  function handleChangeInterestsMatchMode(value: string): void {
    const matchMode = value as TPredicateMode;
    for (const t of interestTargetings) {
      // Add groupId inside the loop to ensure it's unique for each interest
      const groupId = getGroupIdForInterest(matchMode);
      t.set({ group: groupId });
    }
  }

  const interestsMatchMode = determineInterestsMatchMode(interestTargetings);

  return (
    <Card width={'full'}>
      <Card.Header isCollapsible={true} title={t('Interests')} />
      <Card.Content addVerticalGap={true} isLoading={isLoading}>
        {!isLoading && allInterests.length === 0 ? (
          <EmptyState
            description={t('No interest filters are available for the selected channels.')}
            label={t('No interest filters available')}
            theme={'neutral'}
            width={'full'}
          />
        ) : (
          <>
            <SegmentedControl
              data={[
                { label: t('Any of the following'), value: 'match_any' },
                { label: t('All of the following'), value: 'match_all' },
              ]}
              fullWidth={true}
              name={'mode'}
              onChange={handleChangeInterestsMatchMode}
              value={interestsMatchMode}
            />
            <DebouncedInput<string> defaultValue={search} onChange={handleSearch}>
              {(liveValue, onChangeLiveValue): JSX.Element => (
                <Input
                  isClearable={true}
                  onChange={onChangeLiveValue}
                  placeholder={t('Search by keyword, interest, or audience name')}
                  prefix={<FontAwesomeIcon icon={faSearch} />}
                  type={'text'}
                  value={liveValue}
                />
              )}
            </DebouncedInput>

            {isNotSearching && (
              <motion.div animate={'visible'} initial={'hidden'} key={'segmented-control'}>
                <SegmentedControl
                  data={[
                    { label: t('Suggested'), value: 'suggested' },
                    ...categories.map((category) => ({
                      label: category,
                      value: category,
                    })),
                  ]}
                  onChange={handleCategoryChange}
                  value={selectedCategory}
                />
              </motion.div>
            )}

            {!isNotSearching && (
              <Group justify={'space-between'}>
                {hasResults && (
                  <Heading order={3} variant={'h5'}>
                    {t('{{count}} result found', { count: numSearchResults })}
                  </Heading>
                )}

                {hasNoResults && (
                  <EmptyState
                    description={t(
                      'Try checking your spelling, using broader terms, or searching by category.',
                    )}
                    label={t('No results found for "{{search}}"', { search })}
                    theme={'neutral'}
                    width={'full'}
                  />
                )}
              </Group>
            )}

            {filteredInterests && filteredInterests.length > 0 ? (
              <AsyncDisplay isLoading={isSearching}>
                <motion.div
                  animate={'visible'}
                  initial={'hidden'}
                  key={`interests-${filteredInterests.length}-${search}`}
                  variants={INTERESTS_ANIMATIONS.containerVariants}
                >
                  <Stack gap={theme.spacing['2']}>
                    {filteredInterests.map((interest, index) => {
                      const isSelected = selectedInterestIds.includes(interest.id);
                      const interestData = interest.get('interest');
                      return (
                        interestData && (
                          <motion.div
                            custom={index}
                            key={interest.id}
                            variants={INTERESTS_ANIMATIONS.itemVariants}
                          >
                            <Checkbox
                              helpPlacement={'bottom'}
                              label={
                                <Stack gap={0}>
                                  <Text.DefaultMedium>{interestData.name}</Text.DefaultMedium>
                                  <Text.DefaultRegular>
                                    {interestData.description}
                                  </Text.DefaultRegular>
                                </Stack>
                              }
                              layout={'well'}
                              name={'interests'}
                              onChange={(checked): void =>
                                handleInterestChange(checked, interest.id)
                              }
                              value={isSelected}
                            />
                          </motion.div>
                        )
                      );
                    })}
                  </Stack>
                </motion.div>
              </AsyncDisplay>
            ) : null}
          </>
        )}
      </Card.Content>
    </Card>
  );
}

export default observer(Interests);
